// @ts-check

import moment from "moment";
import isFinite from "lodash/isFinite";
import set from "lodash/set";

import { findField } from "./find-field";
import { getRowMatchKeys } from "./configure-matches/get-row-matches";
import { getSelectFieldOptionsCache } from "./get-select-field-options-cache";
import { mergeEntityWithPrevious } from "./merge-entity-with-previous";
import { normalize } from "./are-headers-equal";
import { splitMultiMatchKeys } from "./configure-matches/get-relationship-match-keys";
import { validateByType } from "./entity-validation";
import { smartTrim } from "./smart-trim";

/**
 * @typedef {import("@evolved/domain").FieldType} FieldType
 */

/**
 * @param {string} value
 */
const parseDate = (value) => {
  const parsed = moment(value);

  if (!parsed.isValid()) {
    return {
      error: "Cannot parse date value. Try a format such as 1/30/2021 13:00:00",
    };
  }

  return {
    value: parsed.unix(),
  };
};

/**
 * @type {{
 *   [K in FieldType]?: (value: string) => ({value: any} | {error: string})
 * }}
 */
const conversions = {
  ACTIVITY_DATE: parseDate,
  DOLLAR: (value) => {
    if (!value) {
      return { value: undefined };
    }

    const parsed = parseFloat(value.replace(/,|\s|\$/g, ""));

    if (!isFinite(parsed)) {
      return {
        error:
          "Cannot parse value to dollar. Valid Examples: [100, $100, $100,100.10]",
      };
    }

    return { value: parsed };
  },
  DATE: parseDate,
  FOLLOWUP_DATE: parseDate,
  PERCENT: (value) => {
    let parsed = Number(value.replace("%", ""));

    if (!isFinite(parsed)) {
      return {
        error: `Cannot parse percent value. Must be a percentage between 0 and 100`,
      };
    }

    // TODO: I don't think we can make this assumption
    // if (parsed < 1) {
    //   parsed = parsed * 100;
    // }

    if (parsed < 0 || parsed > 100) {
      return {
        error: `Cannot parse percent value. Must be a percentage between 0 and 100`,
      };
    }

    return {
      value: parsed,
    };
  },
};

/**
 * @param {Object} o
 * @param {import("./domain").ImportableEntityTypes} o.entityType
 * @param {import("./domain").ImportField[]} o.importFields
 * @param {any[]} o.entityCache
 * @param {import("./domain").CSVUpload} o.upload
 * @param {(string | null)[]} o.headerDataIndexes
 * @param {import("./domain").EntityMatchConfig} o.entityMatchConfig
 *
 * @param {{
 *  entitiesByMatchKeys: Record<string, string[]>;
 *  rowsByMatchKeys: Record<string, number[]>;
 * }} o.entityMatchKeys
 *
 * @param {Record<string, {
 *  entitiesByMatchKeys: Record<string, string[]>[];
 *  fieldsByDataIndex: string[];
 * }>} o.relationshipMatchKeys
 *
 * @returns {{
 *  reducedRows: import("./domain").ReducedRow[];
 *  selectOptionsUpdate: Record<string, {id: string; label: string;}[]>;
 * }}
 */

export const reduceImportEntities = ({
  // NOTE: importFields should have been reduced with newUserDefinedFields already
  entityType,
  importFields,
  entityCache,
  upload,
  headerDataIndexes,
  entityMatchConfig,
  entityMatchKeys,
  relationshipMatchKeys,
}) => {
  const selectFieldOptionsCache = getSelectFieldOptionsCache({
    headerDataIndexes,
    importFields,
  });

  const { entitiesByMatchKeys, rowsByMatchKeys } = entityMatchKeys;

  /** @type{Record<string, {id: string; label: string;}[]>} */
  const selectOptionsUpdate = {};

  // TODO:
  // - validate that rows to be created have all
  // required fields
  // - validate all fields in general to ensure they
  // are valid (and won't fail on server side)

  /**
   * @param {string[]} row
   *
   * @returns {import("./domain").ReducedRow}
   */
  const reducer = (row) => {
    /** @type {import("./domain").ReducedRow} */
    const reducedRow = {
      entity: {
        userDefinedFields: {},
      },
      info: [],
      // NOTE: true until proven false
      isValid: true,
    };

    /**
     * @type {import("./domain").EntityMatchInfo['matches']}
     */
    const entityMatches = getRowMatchKeys({
      row,
      headerDataIndexes,
      dataIndexes: entityMatchConfig.dataIndexes,
    }).map((key, index) => {
      const field = entityMatchConfig.dataIndexes[index]
        .filter((dataIndex) => !!dataIndex)
        .map((dataIndex) => findField(dataIndex)(importFields).title)
        .join(" ");

      return {
        key,
        field,
        rowIndexes: rowsByMatchKeys[key] ?? [],
        entityIds: entitiesByMatchKeys[key] ?? [],
      };
    });

    // NOTE: a entity can match IF:
    // - at least one match is valid
    // - a valid match must contain exactly 1 rowIndex and 1 entityId,
    // otherwise, ambiguous
    // - if more than one match is valid, all have the same entityId
    // - empty rowIndex is fine because wouldn't match any entities, so would
    // create
    const validMatchIds = new Set(
      entityMatches
        .filter((match) => {
          return match.rowIndexes.length === 1 && match.entityIds.length === 1;
        })
        .map(({ entityIds }) => entityIds[0])
    );

    const isPotentialMatch = entityMatches.some(({ entityIds }) => {
      return entityIds.length > 0;
    });

    // NOTE:
    // - if not exact match, more than one exact match to different entities,
    // ambiguous
    const isExactMatch = validMatchIds.size === 1;

    if (isExactMatch) {
      const _id = [...validMatchIds.values()][0];

      // TODO: if we do merge for relationships...
      // - will need some logic here to properly merge
      reducedRow.entity._id = _id;
      reducedRow.previous = entityCache.find((entity) => {
        return entity._id === _id;
      });

      if (!reducedRow.previous) {
        throw new Error(`Failed to find entity with id "${_id}"`);
      }
    }

    reducedRow.info.push({
      type: "entity_match_info",
      // NOTE: if any potential matches, must be an exact match,
      // otherwise, simply create the entity.
      level: !isPotentialMatch || isExactMatch ? "info" : "error",
      matches: entityMatches,
    });

    for (const [index, dataIndex] of Object.entries(headerDataIndexes)) {
      if (!dataIndex) {
        continue;
      }

      const raw = row[Number(index)];
      const field = findField(dataIndex)(importFields);

      if (!field.canEdit) {
        continue;
      }

      const parsed = (
        conversions[field.type] ??
        (() => ({
          value: raw,
        }))
      )(raw);

      if ("error" in parsed) {
        reducedRow.info.push({
          type: "value_conversion_error",
          level: "warning",
          headerIndex: index,
          error: parsed.error,
        });
        continue;
      }

      if (field.type === "SELECT") {
        // TODO: update user defined fields with new select
        // values. Will need to update view new user defined
        // fields maybe? Or a specific payload?
        const options = selectFieldOptionsCache[Number(index)];

        if (!options) {
          throw new Error(
            `Select field at headerIndex = ${index} has no options.`
          );
        }

        /**
         * @param {string} value
         */
        const getId = (value) => {
          value = smartTrim(value);

          if (!value) {
            return;
          }

          let id = options[normalize(value)];

          if (!id) {
            id = crypto.randomUUID();
            const udfId = dataIndex.split(".")[1];

            if (!udfId) {
              throw new Error("udf id not found on SELECT field");
            }

            options[normalize(value)] = id;
            selectOptionsUpdate[udfId] = [
              ...(selectOptionsUpdate[udfId] ?? []),
              { id, label: value },
            ];
          }

          return id;
        };

        if (field.multiSelect) {
          set(
            reducedRow.entity,
            dataIndex,
            parsed.value
              .split(",")
              .map(getId)
              .filter((v) => !!v)
          );
        } else {
          set(reducedRow.entity, dataIndex, getId(parsed.value));
        }
      } else if (field.type === "TEXT") {
        // TODO: validate length?
        set(reducedRow.entity, dataIndex, parsed.value);
      } else if (field.type === "SET") {
        const splitValues = splitMultiMatchKeys({
          value: parsed.value,
        });

        /** @type {("cardinality_overflow" | "overwrite_existing_with_nothing")[]} */
        const warnings = [];

        if (
          field.relationship.cardinality === "one" &&
          splitValues.length > 1
        ) {
          warnings.push("cardinality_overflow");
        }

        const { entitiesByMatchKeys, fieldsByDataIndex } =
          relationshipMatchKeys[dataIndex];

        /**
         * @type {import("./domain").RelationshipMatchInfo['splitValues']}
         */
        const splitValuesInfo = splitValues.map((value) => {
          const matchesByIndex = entitiesByMatchKeys.map((byIndex, index) => {
            return {
              entityIds: byIndex[value] ?? [],
              field: fieldsByDataIndex[index],
            };
          });

          const validMatchIds = new Set([
            ...matchesByIndex
              .filter((matches) => {
                return matches.entityIds.length === 1;
              })
              .map(({ entityIds }) => entityIds[0]),
          ]);

          if (validMatchIds.size === 1) {
            return {
              value,
              isValid: true,
              id: [...validMatchIds.values()][0],
              matches: matchesByIndex,
            };
          }

          return {
            value,
            isValid: false,
            matches: matchesByIndex,
          };
        });

        /** @type {string[]} */
        const ids = [];

        for (const info of splitValuesInfo) {
          if (!info.isValid) {
            continue;
          }

          ids.push(info.id);
        }

        // TODO:
        // - ensure _ids are unique
        // - store the info

        set(
          reducedRow.entity,
          dataIndex,
          field.relationship.cardinality === "one"
            ? ids[0] ?? null
            : // TODO: should be handled by server,
              // verify exists check fails when duplicate
              // incoming keys
              [...new Set(ids)]
        );

        reducedRow.info.push({
          type: "relationship_match_info",
          dataIndex,
          headerIndex: index,
          splitValues: splitValuesInfo,
          warnings,
          level:
            warnings.length || splitValuesInfo.some(({ isValid }) => !isValid)
              ? "warning"
              : "info",
        });
      } else if (field.type === "LINK") {
        // TODO: validate
        set(reducedRow.entity, dataIndex, parsed.value);
      } else if (field.type === "DATE") {
        // TODO: validate and map?
        set(reducedRow.entity, dataIndex, parsed.value);
      } else if (field.type === "RANGE") {
        // TODO: implement
        set(reducedRow.entity, dataIndex, parsed.value);
      } else if (field.type === "DOLLAR") {
        // TODO: validate and map
        set(reducedRow.entity, dataIndex, parsed.value);
      } else if (field.type === "NUMBER") {
        // TODO: implement
        set(reducedRow.entity, dataIndex, parsed.value);
      } else if (field.type === "PERCENT") {
        // TODO: implement
        set(reducedRow.entity, dataIndex, parsed.value);
      } else if (field.type === "ACCOUNT_STATE") {
        // TODO: implement
        set(reducedRow.entity, dataIndex, parsed.value);
      } else if (field.type === "ACTIVITY_DATE") {
        // TODO: implement
        set(reducedRow.entity, dataIndex, parsed.value);
      } else if (field.type === "FOLLOWUP_DATE") {
        // TODO: implement
        set(reducedRow.entity, dataIndex, parsed.value);
      } else if (field.type === "CALCULATED") {
        // TODO: implement
        set(reducedRow.entity, dataIndex, parsed.value);
      }
    }

    if (
      // NOTE: no matches, so attempting to create
      !isExactMatch &&
      // NOTE: if any fields are missing,
      // should be logged as entity_validation_error
      !entityMatchConfig.createIfNoMatch
    ) {
      reducedRow.isValid = false;
    }

    // NOTE: it is possible something changed server side,
    // but unlikely, and that will fail, but this gives us
    // a best shot to catch the errors early.
    const validationErrors = validateByType[entityType](
      mergeEntityWithPrevious(reducedRow)
    );
    if (!("success" in validationErrors)) {
      reducedRow.info.push({
        type: "entity_validation_failed",
        level: "error",
        errors: validationErrors.errors,
      });
    }

    if (
      reducedRow.info.some(({ level }) => {
        return level === "error";
      })
    ) {
      reducedRow.isValid = false;
    }

    return reducedRow;
  };

  return {
    reducedRows: upload.rows.map(reducer),
    selectOptionsUpdate,
  };
};
